import {
  type FixedLayoutColors,
  type LineHeight,
  type ReadingPreferences,
  type ReflowableColors,
  type TextAlign,
} from "@msb/reader-bridge";
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { defineMessages, intlLocale, setLocale, useLocale, useT, type Locale } from "../i18n/index.ts";
import { Icon, type IconName } from "../icons/Icon.tsx";

interface Props {
  preferences: ReadingPreferences;
  showAccessibility: boolean;
  /** Livre à mise en page fixe : seulement les couleurs du fond et le nombre de pages, comme l'app et Studio. */
  fixedLayout?: boolean;
  onChange(preferences: ReadingPreferences): void;
}

interface Choice<T> {
  value: T;
  label: string;
  icon?: IconName;
  /** Texte court affiché avec le libellé (FR, EN), lu dans sa langue. */
  short?: string;
  lang?: string;
}

/*
 * Même feuille « Réglages » que l'app (PreferencesSheet.kt) et Studio : sections « Caractères », « Couleurs »,
 * « Colonnes », « Alignement et césure », « Interligne » et « Accessibilité », chacune dans une carte.
 * Pas encore de choix de police ni de mode défilement : le reader Web ne charge pas les polices de l'app
 * et ne sait pas faire défiler le texte.
 */
const messages = defineMessages(
  {
    auto: "Auto",
    light: "Clair",
    dark: "Sombre",
    sepia: "Sépia",
    oneColumn: "Une colonne",
    default: "Par défaut",
    justify: "Justifié",
    left: "À gauche",
    right: "À droite",
    small: "Petit",
    medium: "Moyen",
    large: "Grand",
    letterSpacing: "Espace entre les lettres",
    wordSpacing: "Espace entre les mots",
    paragraphSpacing: "Espace entre les paragraphes",
    decrease: "Diminuer : {label}",
    increase: "Augmenter : {label}",
    colors: "Couleurs",
    characters: "Caractères",
    textSize: "Taille de caractère",
    percent: "{value} %",
    columns: "Colonnes",
    alignmentAndHyphens: "Alignement et césure",
    alignment: "Alignement",
    hyphens: "Activer la césure",
    lineHeight: "Interligne",
    accessibility: "Accessibilité",
    accessibilityOptions: "Activer les options d'accessibilité",
    moreOptions: "Plus d'options",
    reset: "Réinitialiser",
    pages: "Pages",
    onePage: "Une page",
    twoPages: "Deux pages",
  },
  {
    auto: "Auto",
    light: "Light",
    dark: "Dark",
    sepia: "Sepia",
    oneColumn: "One column",
    default: "Default",
    justify: "Justified",
    left: "Left",
    right: "Right",
    small: "Small",
    medium: "Medium",
    large: "Large",
    letterSpacing: "Letter spacing",
    wordSpacing: "Word spacing",
    paragraphSpacing: "Paragraph spacing",
    decrease: "Decrease: {label}",
    increase: "Increase: {label}",
    colors: "Colours",
    characters: "Text",
    textSize: "Text size",
    percent: "{value}%",
    columns: "Columns",
    alignmentAndHyphens: "Alignment and hyphenation",
    alignment: "Alignment",
    hyphens: "Enable hyphenation",
    lineHeight: "Line spacing",
    accessibility: "Accessibility",
    accessibilityOptions: "Enable accessibility options",
    moreOptions: "More options",
    reset: "Reset",
    pages: "Pages",
    onePage: "One page",
    twoPages: "Two pages",
  },
);

const languages: Array<Choice<Locale>> = [
  { value: "fr", label: "Français", short: "FR", lang: "fr" },
  { value: "en", label: "English", short: "EN", lang: "en" },
];

/** Taille du texte en %, comme le curseur de l'app (80 à 170, par pas de 10). */
const MIN_SIZE = 80;
const MAX_SIZE = 170;
const SIZE_STEP = 10;

/** Espacements de l'accessibilité : pas et maximum de PreferencesViewModel.kt (l'affichage ajoute 1). */
const spacings = {
  letterSpacing: { step: 0.05, max: 0.5 },
  wordSpacing: { step: 0.1, max: 1.0 },
  paragraphSpacing: { step: 0.25, max: 3.0 },
} as const;
const DELTA = 0.001;

const formatSpacing = (value: number, intl: string) =>
  (value + 1).toLocaleString(intl, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Section({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <section class="msb-pref">
      <h3 class="msb-pref__label">{label}</h3>
      <div class="msb-pref-card">{children}</div>
    </section>
  );
}

/** Boutons de l'app : deux par ligne, icône et libellé, le choix en cours en couleur d'accent. */
function ButtonGroup<T>({ label, choices, value, onSelect }: { label: string; choices: Array<Choice<T>>; value: T; onSelect(v: T): void }) {
  return (
    <div class="msb-choices" role="radiogroup" aria-label={label}>
      {choices.map((c) => (
        <button
          type="button"
          key={c.label}
          role="radio"
          lang={c.lang}
          aria-checked={c.value === value}
          class="msb-choice"
          onClick={() => onSelect(c.value)}
        >
          {c.icon && <Icon name={c.icon} size={28} />}
          {c.short && <strong aria-hidden="true">{c.short}</strong>}
          <span>{c.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Curseur à paliers de l'app (MsbSlider, Material 3) : deux pistes arrondies séparées par une poignée,
 * un point à chaque palier. Le vrai `<input type="range">` reste au-dessus, invisible : clavier, toucher et
 * lecteurs d'écran fonctionnent comme d'habitude.
 */
function StepSlider({ label, min, max, step, value, valueText, onChange }: { label: string; min: number; max: number; step: number; value: number; valueText: string; onChange(value: number): void }) {
  const steps = Math.round((max - min) / step);
  const index = Math.round((value - min) / step);
  const fraction = steps > 0 ? index / steps : 0;
  return (
    <div class="msb-stepslider" style={{ "--f": fraction } as JSX.CSSProperties}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        aria-valuetext={valueText}
        onInput={(e) => onChange(Number(e.currentTarget.value))}
      />
      <span class="msb-stepslider__track msb-stepslider__track--active" aria-hidden="true" />
      <span class="msb-stepslider__track msb-stepslider__track--inactive" aria-hidden="true" />
      {Array.from({ length: steps + 1 }, (_, i) =>
        i === index ? null : (
          <span
            key={i}
            class={i < index ? "msb-stepslider__tick msb-stepslider__tick--active" : "msb-stepslider__tick"}
            style={{ "--t": i / steps } as JSX.CSSProperties}
            aria-hidden="true"
          />
        ),
      )}
      <span class="msb-stepslider__handle" aria-hidden="true" />
    </div>
  );
}

function Row({ children, class: className = "" }: { children: ComponentChildren; class?: string }) {
  return <div class={`msb-pref-row ${className}`}>{children}</div>;
}

function Switch({ label, checked, onChange }: { label: string; checked: boolean; onChange(checked: boolean): void }) {
  return (
    <label class="msb-pref-row msb-switch">
      <span>{label}</span>
      <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.currentTarget.checked)} />
    </label>
  );
}

/** Moins / plus reliés, comme le Stepper de l'app. */
function Stepper({ label, canDecrease, canIncrease, onDecrease, onIncrease }: { label: string; canDecrease: boolean; canIncrease: boolean; onDecrease(): void; onIncrease(): void }) {
  const t = useT(messages);
  return (
    <div class="msb-stepper2">
      <button type="button" aria-label={t("decrease", { label })} disabled={!canDecrease} onClick={onDecrease}>
        <Icon name="remove" />
      </button>
      <button type="button" aria-label={t("increase", { label })} disabled={!canIncrease} onClick={onIncrease}>
        <Icon name="add" />
      </button>
    </div>
  );
}

function SpacingRow({ field, value, enabled, onChange }: { field: keyof typeof spacings; value: number; enabled: boolean; onChange(value: number): void }) {
  const t = useT(messages);
  const locale = useLocale();
  const label = t(field);
  const { step, max } = spacings[field];
  const round = (v: number) => Math.round(v * 1000) / 1000;
  return (
    <Row class={enabled ? "" : "msb-pref-row--disabled"}>
      <span class="msb-pref-row__label">{label}</span>
      <span class="msb-pref-row__value">{formatSpacing(value, intlLocale(locale))}</span>
      <Stepper
        label={label}
        canDecrease={enabled && value > DELTA}
        canIncrease={enabled && value < max - DELTA}
        onDecrease={() => onChange(Math.max(0, round(value - step)))}
        onIncrease={() => onChange(Math.min(max, round(value + step)))}
      />
    </Row>
  );
}

function LanguageSection() {
  const locale = useLocale();
  return (
    <Section label="Langue / Language">
      <ButtonGroup label="Langue / Language" choices={languages} value={locale} onSelect={setLocale} />
    </Section>
  );
}

/** Mêmes réglages que la feuille « Réglages » de l'app et de Studio. */
export function PreferencesPanel({ preferences, showAccessibility, fixedLayout = false, onChange }: Props) {
  const t = useT(messages);
  const update = (patch: Partial<ReadingPreferences>) => onChange({ ...preferences, ...patch });

  if (fixedLayout) {
    const fixedThemes: Array<Choice<FixedLayoutColors | null>> = [
      { value: null, label: t("auto"), icon: "themeSystem" },
      { value: "Light", label: t("light"), icon: "themeLight" },
      { value: "Dark", label: t("dark"), icon: "themeDark" },
    ];
    const spreads: Array<Choice<boolean | null>> = [
      { value: null, label: t("auto"), icon: "columnsAuto" },
      { value: false, label: t("onePage"), icon: "columnsOne" },
      { value: true, label: t("twoPages"), icon: "columnsTwo" },
    ];
    return (
      <div class="msb-prefs">
        <Section label={t("colors")}>
          <ButtonGroup label={t("colors")} choices={fixedThemes} value={preferences.fixedColors} onSelect={(fixedColors) => update({ fixedColors })} />
        </Section>
        <Section label={t("pages")}>
          <ButtonGroup label={t("pages")} choices={spreads} value={preferences.spreads} onSelect={(value) => update({ spreads: value })} />
        </Section>
        <LanguageSection />
      </div>
    );
  }

  const size = Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(preferences.fontSize * 100)));
  const themes: Array<Choice<ReflowableColors | null>> = [
    { value: null, label: t("auto"), icon: "themeSystem" },
    { value: "Light", label: t("light"), icon: "themeLight" },
    { value: "Dark", label: t("dark"), icon: "themeDark" },
    { value: "Sepia", label: t("sepia"), icon: "themeSepia" },
  ];
  const columns: Array<Choice<1 | null>> = [
    { value: null, label: t("auto"), icon: "columnsAuto" },
    { value: 1, label: t("oneColumn"), icon: "columnsOne" },
  ];
  const alignments: Array<Choice<TextAlign | null>> = [
    { value: null, label: t("default"), icon: "reset" },
    { value: "Justify", label: t("justify"), icon: "alignJustify" },
    { value: "Left", label: t("left"), icon: "alignLeft" },
    { value: "Right", label: t("right"), icon: "alignRight" },
  ];
  const lineHeights: Array<Choice<LineHeight | null>> = [
    { value: null, label: t("default"), icon: "reset" },
    { value: "Small", label: t("small"), icon: "lineSmall" },
    { value: "Medium", label: t("medium"), icon: "lineMedium" },
    { value: "Large", label: t("large"), icon: "lineLarge" },
  ];

  return (
    <div class="msb-prefs">
      <Section label={t("characters")}>
        <Row>
          <StepSlider
            label={t("textSize")}
            min={MIN_SIZE}
            max={MAX_SIZE}
            step={SIZE_STEP}
            value={size}
            valueText={t("percent", { value: size })}
            onChange={(value) => update({ fontSize: value / 100 })}
          />
        </Row>
      </Section>

      <Section label={t("colors")}>
        <ButtonGroup label={t("colors")} choices={themes} value={preferences.colors} onSelect={(colors) => update({ colors })} />
      </Section>

      <Section label={t("columns")}>
        <ButtonGroup label={t("columns")} choices={columns} value={preferences.columnCount} onSelect={(columnCount) => update({ columnCount })} />
      </Section>

      <Section label={t("alignmentAndHyphens")}>
        <ButtonGroup label={t("alignment")} choices={alignments} value={preferences.textAlign} onSelect={(textAlign) => update({ textAlign })} />
        <Switch label={t("hyphens")} checked={preferences.hyphens} onChange={(hyphens) => update({ hyphens })} />
      </Section>

      <Section label={t("lineHeight")}>
        <ButtonGroup label={t("lineHeight")} choices={lineHeights} value={preferences.lineHeight} onSelect={(lineHeight) => update({ lineHeight })} />
      </Section>

      {showAccessibility && (
        <Section label={t("accessibility")}>
          <Switch
            label={t("accessibilityOptions")}
            checked={preferences.enableAccessibilityOptions}
            onChange={(enableAccessibilityOptions) => update({ enableAccessibilityOptions })}
          />
          {(Object.keys(spacings) as Array<keyof typeof spacings>).map((field) => (
            <SpacingRow
              key={field}
              field={field}
              value={preferences[field]}
              enabled={preferences.enableAccessibilityOptions}
              onChange={(value) => update({ [field]: value })}
            />
          ))}
        </Section>
      )}

      <LanguageSection />
    </div>
  );
}

/** Menu « ⋯ » de l'en-tête de la feuille, avec « Réinitialiser », comme dans l'app et Studio. */
export function PreferencesMenu({ onReset }: { onReset(): void }) {
  const t = useT(messages);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  return (
    <div class="msb-prefs-menu" ref={box}>
      <button type="button" class="msb-icon-button" aria-haspopup="menu" aria-expanded={open} aria-label={t("moreOptions")} onClick={() => setOpen(!open)}>
        <Icon name="more" />
      </button>
      {open && (
        <div class="msb-prefs-menu__list" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onReset();
            }}
          >
            {t("reset")}
          </button>
        </div>
      )}
    </div>
  );
}
